import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Select, App } from "antd";
import { PictureOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import dayjs from "../../../utils/formacion/dayjs-timezone";
import PageHeader from "../../../components/PageHeader";
import DataTable from "../../../components/DataTable";
import TableSearchBar from "../../../components/TableSearchBar";
import ActivityIndicator from "../../../components/ActivityIndicator";
import useDebounce from "../../../hooks/useDebounce";
import { useClampPageToTotal } from "../../../hooks/useClampPageToTotal";
import { useIsInitialLoad } from "../../../hooks/useIsInitialLoad";
import { useSchools } from "../../../hooks/useSchools";
import { useSearchParams } from "react-router-dom";
import {
  useDeleteTrainingMutation,
  useGetTrainingsPaginatedQuery,
} from "../../../store/api/formacionApi";
import {
  TrainingAudienceFilter,
  TrainingRow,
  TrainingSortBy,
  TrainingSortDir,
  TrainingStatus,
} from "../../../services/formacion/formacion.types";
import { resolveTrainingStatus } from "../../../utils/formacion/training-status";
import {
  ALL_SCHOOLS,
  ALL_SCHOOLS_LABEL,
  AUDIENCE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  TIMEZONE_LABELS,
  formatAudiencePhrase,
} from "../../../utils/formacion/training-display";
import { getApiErrorMessage } from "../../../utils";
import TrainingStatusChip from "./TrainingStatusChip";
import TrainingModal from "./TrainingModal";
import RecordingUploadModal from "./RecordingUploadModal";
import CoverModal from "./CoverModal";

const DEFAULT_PAGE_SIZE = 10;
export { ALL_SCHOOLS, ALL_SCHOOLS_LABEL };

const FormacionPage: React.FC = () => {
  const { notification } = App.useApp();
  const { schools } = useSchools();
  const [searchParams, setSearchParams] = useSearchParams();

  const schoolId = parseInt(searchParams.get("schoolId") ?? "", 10) || null;
  const page = parseInt(searchParams.get("page") ?? "", 10) || 1;
  const pageSize =
    parseInt(searchParams.get("pageSize") ?? "", 10) || DEFAULT_PAGE_SIZE;
  const searchParam = searchParams.get("search") ?? "";
  const audienceParam =
    (searchParams.get("audience") as TrainingAudienceFilter | null) ??
    undefined;
  const statusParam =
    (searchParams.get("status") as TrainingStatus | null) ?? undefined;
  const sortByParam =
    (searchParams.get("sortBy") as TrainingSortBy | null) ?? undefined;
  const sortDirParam =
    (searchParams.get("sortDir") as TrainingSortDir | null) ?? undefined;

  const schoolName =
    schoolId === null
      ? ALL_SCHOOLS_LABEL
      : schools.find((school) => school.id === schoolId)?.name ?? "";

  const [search, setSearch] = useState(searchParam);
  const debouncedSearch = useDebounce(search, 500);

  const [modalTraining, setModalTraining] = useState<TrainingRow | null>(
    null
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [recordingTraining, setRecordingTraining] =
    useState<TrainingRow | null>(null);
  const [coverTraining, setCoverTraining] = useState<TrainingRow | null>(null);

  const [modal, modalContextHolder] = Modal.useModal();

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const currentSearch = prev.get("search") ?? "";
        if (currentSearch === debouncedSearch) return prev;
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set("search", debouncedSearch);
        else next.delete("search");
        next.delete("page");
        return next;
      },
      { replace: true }
    );
  }, [debouncedSearch, setSearchParams]);

  const { data, isFetching, isError, refetch } = useGetTrainingsPaginatedQuery(
    {
      schoolId,
      page,
      pageSize,
      search: searchParam || undefined,
      audience: audienceParam,
      status: statusParam,
      sortBy: sortByParam,
      sortDir: sortDirParam,
    }
  );
  const [deleteTraining] = useDeleteTrainingMutation();

  const trainings = data?.data ?? [];
  const pagination = data?.pagination ?? { page, pageSize, total: 0 };
  const isInitialLoad = useIsInitialLoad(isFetching, trainings);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      next.set("pageSize", String(newPageSize));
      return next;
    });
  };

  useClampPageToTotal({
    total: pagination.total,
    pageSize,
    page,
    onPageChange: (lastPage) => handlePageChange(lastPage, pageSize),
  });

  const handleSchoolChange = (value: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === ALL_SCHOOLS) next.delete("schoolId");
      else next.set("schoolId", String(value));
      next.delete("page");
      return next;
    });
  };

  const handleAudienceChange = (value: TrainingAudienceFilter | "ALL") => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === "ALL") next.delete("audience");
      else next.set("audience", value);
      next.delete("page");
      return next;
    });
  };

  const handleStatusChange = (value: TrainingStatus | "ALL") => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === "ALL") next.delete("status");
      else next.set("status", value);
      next.delete("page");
      return next;
    });
  };

  const handleSorterChange = (
    sorter: SorterResult<TrainingRow> | SorterResult<TrainingRow>[]
  ) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!single?.order || single.columnKey !== "title") {
        next.delete("sortBy");
        next.delete("sortDir");
      } else {
        next.set("sortBy", "title");
        next.set("sortDir", single.order === "ascend" ? "asc" : "desc");
      }
      return next;
    });
  };

  const handleDelete = (record: TrainingRow) => {
    modal.confirm({
      title: `Eliminar "${record.title}"`,
      content:
        "Al eliminar esta capacitación también se elimina la grabación asociada. Esta acción no se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await deleteTraining({ id: record.id, schoolId }).unwrap();
          notification.success({
            message: "Éxito",
            description: "La capacitación fue eliminada exitosamente!",
            placement: "top",
            duration: 3,
          });
        } catch (error: any) {
          notification.error({
            message: "Eliminación erronea",
            description: getApiErrorMessage(
              error,
              "La capacitación no pudo ser eliminada!"
            ),
            placement: "top",
            duration: 4,
          });
        }
      },
    });
  };

  const columns: ColumnsType<TrainingRow> = useMemo(
    () => [
      {
        title: "Título",
        dataIndex: "title",
        key: "title",
        sorter: true,
        sortOrder:
          sortByParam === "title"
            ? sortDirParam === "asc"
              ? "ascend"
              : "descend"
            : undefined,
      },
      {
        title: "Audiencia",
        key: "audience",
        render: (_, record) => formatAudiencePhrase(record),
      },
      {
        title: "Fecha y hora",
        key: "starts_at",
        render: (_, record) => {
          const startsAt = dayjs(record.starts_at).tz(record.timezone);
          const tzLabel = TIMEZONE_LABELS[record.timezone] ?? record.timezone;
          return (
            <div>
              <div>{startsAt.format("D [de] MMM YYYY, h:mm a")}</div>
              <div style={{ color: "var(--bo-text-secondary)", fontSize: 12 }}>
                {tzLabel}
              </div>
            </div>
          );
        },
      },
      {
        title: "Estado",
        key: "status",
        render: (_, record) => (
          <TrainingStatusChip
            status={resolveTrainingStatus(record, new Date())}
          />
        ),
      },
      {
        title: "Webinar",
        key: "webinar",
        render: (_, record) => {
          const status = resolveTrainingStatus(record, new Date());
          if (status === "SCHEDULED") {
            return (
              <a href={record.registration_url} target="_blank" rel="noreferrer">
                Inscribirse
              </a>
            );
          }
          if (status === "LIVE") {
            return (
              <a href={record.join_url} target="_blank" rel="noreferrer">
                Unirse al webinar
              </a>
            );
          }
          if (record.recording_url) {
            return (
              <a href={record.recording_url} target="_blank" rel="noreferrer">
                Ver grabación
              </a>
            );
          }
          return (
            <Button size="small" onClick={() => setRecordingTraining(record)}>
              Subir grabación
            </Button>
          );
        },
      },
    ],
    [sortByParam, sortDirParam]
  );

  return (
    <div>
      <PageHeader
        title="Formación"
        extra={
          <TableSearchBar
            fields={[
              {
                placeholder: "Buscar por título",
                value: search,
                onChange: setSearch,
                width: 240,
              },
            ]}
          >
            <Select
              style={{ width: 280 }}
              prefix="Colegio"
              aria-label="Colegio"
              value={schoolId ?? ALL_SCHOOLS}
              onChange={handleSchoolChange}
              options={[
                { value: ALL_SCHOOLS, label: ALL_SCHOOLS_LABEL },
                ...schools.map((school) => ({
                  value: school.id,
                  label: school.name,
                })),
              ]}
            />
            <Select
              style={{ width: 220 }}
              prefix="Audiencia"
              aria-label="Audiencia"
              value={audienceParam ?? "ALL"}
              onChange={handleAudienceChange}
              options={AUDIENCE_FILTER_OPTIONS}
            />
            <Select
              style={{ width: 200 }}
              prefix="Estado"
              aria-label="Estado"
              value={statusParam ?? "ALL"}
              onChange={handleStatusChange}
              options={STATUS_FILTER_OPTIONS}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateOpen(true)}
            >
              Nueva capacitación
            </Button>
          </TableSearchBar>
        }
      />
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No se pudieron cargar las capacitaciones."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}
      {isInitialLoad ? (
        <ActivityIndicator />
      ) : (
        <DataTable
          data={trainings}
          columns={columns}
          onEdit={(record) => setModalTraining(record)}
          onDelete={handleDelete}
          canEdit={(record) =>
            resolveTrainingStatus(record, new Date()) !== "COMPLETED"
          }
          extraActions={(record) =>
            resolveTrainingStatus(record, new Date()) === "COMPLETED" ? (
              <Button
                icon={<PictureOutlined />}
                onClick={() => setCoverTraining(record)}
              >
                Editar portada
              </Button>
            ) : null
          }
          skipDeleteConfirm
          onSorterChange={handleSorterChange}
          loading={isFetching}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: handlePageChange,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30"],
          }}
        />
      )}
      {(isCreateOpen || modalTraining) && (
        <TrainingModal
          open
          schoolId={schoolId}
          schoolName={schoolName}
          schools={schools}
          training={modalTraining}
          onClose={() => {
            setIsCreateOpen(false);
            setModalTraining(null);
          }}
          onSaved={() => {
            setIsCreateOpen(false);
            setModalTraining(null);
            refetch();
          }}
        />
      )}
      {coverTraining && (
        <CoverModal
          open
          schoolId={schoolId}
          training={coverTraining}
          onClose={() => setCoverTraining(null)}
          onSaved={() => {
            setCoverTraining(null);
            refetch();
          }}
        />
      )}
      {recordingTraining && (
        <RecordingUploadModal
          open
          schoolId={schoolId}
          training={recordingTraining}
          onClose={() => setRecordingTraining(null)}
          onSaved={() => {
            setRecordingTraining(null);
            refetch();
          }}
        />
      )}
      {modalContextHolder}
    </div>
  );
};

export default FormacionPage;
